import React, {type FC} from 'react';
import s from './ProductMediaDots.scss';
import classNames from 'classnames';
import {useProductGallery} from '../../ProductGalleryProvider/ProductGalleryProvider';
import type {IMediaItem} from '../../../../types/app-types';
import {type ProvidedGlobalProps, withGlobalProps} from '../../../../providers/globalPropsProvider';
import {
  type IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {DotNavigation, DotNavigationTheme} from 'wix-ui-tpa/cssVars';

export enum ProductMediaDotsDataHooks {
  Root = 'product-media-dots-root',
  Dot = 'product-media-dot',
  NavigationDots = 'product-media-dots-navigation',
}

export interface ProductMediaDotsProps extends ProvidedGlobalProps, IProvidedTranslationProps {
  media: IMediaItem[];
}

const ProductMediaDotsComponent: FC<ProductMediaDotsProps> = ({media, t, globals: {product, experiments}}) => {
  const {selectedIndex, changeSelectedIndex} = useProductGallery();

  const isProductMediaNavigationDotsEnabled = experiments.productMediaNavigationDots;

  const ariaLabelsArray = media.map((currentMediaItem) =>
    t('productPage.gallery.bullet.ariaLabel', {
      productName: currentMediaItem?.altText || product.name,
    })
  );

  return (
    <div className={s.root} data-hook={ProductMediaDotsDataHooks.Root}>
      {isProductMediaNavigationDotsEnabled ? (
        <DotNavigation
          currentIndex={selectedIndex}
          length={media.length}
          onSelect={changeSelectedIndex}
          data-hook={ProductMediaDotsDataHooks.NavigationDots}
          showBorder={true}
          theme={DotNavigationTheme.Light}
          aria-label={ariaLabelsArray}
        />
      ) : (
        media.map((mediaItem, index) => {
          const isSelected = selectedIndex === index;
          return (
            <button
              type="button"
              key={index}
              className={classNames(s.dot, {
                [s.isSelected]: isSelected,
              })}
              data-hook={ProductMediaDotsDataHooks.Dot}
              onClick={() => changeSelectedIndex(index)}
              aria-pressed={isSelected}
              aria-label={t('productPage.gallery.bullet.ariaLabel', {productName: mediaItem.altText || product.name})}
            />
          );
        })
      )}
    </div>
  );
};

export const ProductMediaDots = withGlobalProps(withTranslations('globals.texts')(ProductMediaDotsComponent));
